import { useState } from 'react'
import type { AvatarPresetId } from '../avatarPresets'
import type { Rival, RivalRequest, LeagueMember } from '../types'
import { avatarPresetForId } from '../avatarPresets'
import { AvatarGlyph } from './ProfileAvatar'
import './RivalsList.css'

interface RivalsListProps {
  rivals: Rival[]
  /** Incoming pending challenges (toUserId = me) — rendered first, with Accept/Decline actions. */
  incomingRequests?: RivalRequest[]
  /** Provide alongside onInviteToLeague to show a per-league Invite/In league button on each row. */
  standings?: LeagueMember[]
  onInviteToLeague?: (userId: string) => void
  onAccept?: (requestId: string) => void
  onDecline?: (requestId: string) => void
  onViewProfile: (userId: string, displayName: string) => void
  /** Set false to always render expanded, without the collapsing toggle. */
  collapsible?: boolean
  toggleLabel?: string
  emptyMessage?: string
  /** Set false to hide the W-L-T record, e.g. in the league-invite usage where it's irrelevant. */
  showRecord?: boolean
}

/* Same glyph, size and fallback as `RowAvatar` in LeagueDetail: a rivals row and a league standings
   row are the same kind of thing, so they show the same face. Users who never picked a preset get
   the deterministic id-derived one rather than a hole in the column. */
function RowAvatar({ userId, preset }: { userId: string; preset?: AvatarPresetId | null }) {
  return (
    <span className="rivals-list__avatar" aria-hidden="true">
      <AvatarGlyph
        preset={preset ?? avatarPresetForId(userId)}
        className="rivals-list__avatar-glyph"
      />
    </span>
  )
}

const DEFAULT_EMPTY_MESSAGE = 'No rivals yet — challenge someone from a league or their profile.'

function RivalsList({
  rivals,
  incomingRequests = [],
  standings,
  onInviteToLeague,
  onAccept,
  onDecline,
  onViewProfile,
  collapsible = true,
  toggleLabel = 'Rivals',
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  showRecord = true,
}: RivalsListProps) {
  const [isOpen, setIsOpen] = useState(!collapsible)

  const memberIds = new Set((standings ?? []).map((m) => m.id))
  // Sort order: pending incoming challenges first (handled separately below), then active
  // rivals with the most recently active rivalry first — no existing precedent to copy here.
  const sortedRivals = [...rivals].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  const isEmpty = incomingRequests.length === 0 && sortedRivals.length === 0

  return (
    <div className="rivals-list">
      {collapsible && (
        <button
          className="rivals-list__toggle label-plain"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
        >
          {isOpen ? '▾' : '▸'} {toggleLabel}
        </button>
      )}

      {isOpen && (
        <div className="rivals-list__body">
          {isEmpty ? (
            <p className="rivals-list__empty">{emptyMessage}</p>
          ) : (
            <>
              {incomingRequests.map((request) => (
                <div key={request.id} className="rivals-list__item">
                  <RowAvatar userId={request.fromUserId} preset={request.fromAvatarPreset} />
                  <button
                    type="button"
                    className="rivals-list__name"
                    onClick={() => onViewProfile(request.fromUserId, request.fromDisplayName)}
                  >
                    {request.fromDisplayName}
                  </button>
                  <div className="rivals-list__request-actions">
                    <button
                      type="button"
                      className="rivals-list__accept-btn"
                      onClick={() => onAccept?.(request.id)}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="rivals-list__decline-btn"
                      onClick={() => onDecline?.(request.id)}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}

              {sortedRivals.map((rival) => {
                const isMember = memberIds.has(rival.userId)
                return (
                  <div key={rival.userId} className="rivals-list__item">
                    <RowAvatar userId={rival.userId} preset={rival.avatarPreset} />
                    <button
                      type="button"
                      className="rivals-list__name"
                      onClick={() => onViewProfile(rival.userId, rival.displayName)}
                    >
                      {rival.displayName}
                    </button>
                    {showRecord && (
                      <span className="rivals-list__record">
                        <span
                          className={
                            rival.record.wins > 0
                              ? 'rivals-list__record-wins'
                              : 'rivals-list__record-zero'
                          }
                        >
                          {rival.record.wins}W
                        </span>
                        {' - '}
                        <span
                          className={
                            rival.record.losses > 0
                              ? 'rivals-list__record-losses'
                              : 'rivals-list__record-zero'
                          }
                        >
                          {rival.record.losses}L
                        </span>
                        {rival.record.ties > 0 ? ` - ${rival.record.ties}T` : ''}
                      </span>
                    )}
                    {onInviteToLeague && (
                      <button
                        className="rivals-list__invite-btn"
                        disabled={isMember}
                        onClick={() => onInviteToLeague(rival.userId)}
                      >
                        {isMember ? 'In league' : 'Invite'}
                      </button>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default RivalsList
